import { ISplitOptions } from "../../../models/text/ISplitOptions";
import { Token, WrapperInfo } from "../BuildTokens";
import { FontMetrics } from "../CanvasKerningApplier";
import { splitIntoSegments } from "../TextSegmentation";
import { MeasuredToken, MeasurementSource } from "./types";

function wrappersEqual(a?: WrapperInfo[], b?: WrapperInfo[]): boolean {
  if (!a?.length && !b?.length) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((w, i) => w.id === b[i].id);
}

function splitClassEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((cls, i) => cls === b[i]);
}

export function measureTokens(
  tokens: Token[],
  source: MeasurementSource,
  fontMetrics: FontMetrics,
  options: ISplitOptions,
): MeasuredToken[] {
  const range = document.createRange();
  const measured: MeasuredToken[] = [];

  let isAfterElement = false;
  let lastTokenWasSpace = false;
  let prevMeasuredWasText = false;
  let prevTokenWrappers: WrapperInfo[] | undefined = undefined;
  let prevTokenSplitClass: string[] = [];

  try {
    for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
      const token = tokens[tokenIndex];
      switch (token.type) {
        case "br": {
          measured.push({ token, rect: new DOMRect(0, 0, 0, 0) });
          lastTokenWasSpace = true;
          prevMeasuredWasText = false;
          prevTokenWrappers = undefined;
          prevTokenSplitClass = [];
          isAfterElement = false;
          break;
        }

        case "space":
          lastTokenWasSpace = !shouldTrimInlineGap(tokens, tokenIndex, options);
          break;

        case "other":
          prevMeasuredWasText = false;
          lastTokenWasSpace = false;
          break;

        case "text": {
          const raw = token.content;
          const hadLeadingSpace = /^\s/.test(raw);
          const currentWrappers = (token.meta?.wrappers as WrapperInfo[]) ?? [];
          const currentSplitClass: string[] = (token.meta?.splitClass as string[]) ?? [];
          const segments = splitIntoSegments(raw, options);
          let wIdx = 0;

          for (const segment of segments) {
            const word = segment.text;
            let domRect = new DOMRect(0, 0, 0, 0);
            const measureNode = source.resolveNode(token.node) as Text;

            try {
              range.setStart(measureNode, segment.start);
              range.setEnd(measureNode, segment.end);
              domRect = range.getBoundingClientRect();
            } catch (_) {
              // Ignore detached/invalid ranges. The caller will still get a zero rect
              // and line splitting can continue deterministically.
            }

            const wouldJoinPrev =
              wIdx === 0 && !hadLeadingSpace && !lastTokenWasSpace && prevMeasuredWasText;
            const sameWrappers = wIdx > 0 || wrappersEqual(prevTokenWrappers, currentWrappers);
            // Any difference in splitClass (including prev has class, current doesn't)
            // means a split-class boundary — don't join across it.
            const splitClassConflict =
              wIdx === 0 && !splitClassEqual(prevTokenSplitClass, currentSplitClass);

            const accurateWidth = fontMetrics.measureWord(word);
            const rect = new DOMRect(domRect.x, domRect.y, accurateWidth, domRect.height);

            const wordMeta: Record<string, any> = {
              ...(token.meta || {}),
              joinPrev: wouldJoinPrev && sameWrappers && !splitClassConflict,
              noSpaceBefore: segment.noSpaceBefore || (wouldJoinPrev && (!sameWrappers || splitClassConflict)),
            };

            if (isAfterElement && wIdx === 0) {
              wordMeta.isAfterElement = true;
              isAfterElement = false;
            }

            measured.push({
              token: { type: "text", id: "", node: token.node, content: word, meta: wordMeta },
              rect,
              browserWidth: domRect.width,
            });

            wIdx++;
          }

          if (wIdx > 0) {
            lastTokenWasSpace = /\s$/.test(raw);
            prevMeasuredWasText = true;
            prevTokenWrappers = currentWrappers;
            prevTokenSplitClass = currentSplitClass;
          }
          break;
        }

        case "element": {
          const domRect = (source.resolveNode(token.node) as HTMLElement).getBoundingClientRect();
          measured.push({ token, rect: domRect, browserWidth: domRect.width });

          const prev = measured[measured.length - 2];
          if (prev?.token.type === "text") {
            prev.token.meta = { ...(prev.token.meta || {}), isBeforeElement: true };
          }

          isAfterElement = true;
          prevMeasuredWasText = false;
          prevTokenSplitClass = [];
          lastTokenWasSpace = false;
          break;
        }
      }
    }
  } finally {
    range.detach?.();
    source.cleanup();
  }

  return measured;
}

function shouldTrimInlineGap(tokens: Token[], index: number, options: ISplitOptions): boolean {
  if (options.trimInlineGaps !== true) return false;

  const token = tokens[index];
  if (token?.type !== "space") return false;
  if (!/[\n\r\t]/.test(token.content) && token.content.length <= 1) return false;

  const prev = findPrevMeaningfulToken(tokens, index - 1);
  const next = findNextMeaningfulToken(tokens, index + 1);
  if (!prev || !next) return false;

  if (!isInlineJoinCandidate(prev) || !isInlineJoinCandidate(next)) return false;

  const prevWrappers = getWrapperIds(prev);
  const nextWrappers = getWrapperIds(next);
  if (prevWrappers.length === 0 || nextWrappers.length === 0) return false;

  return prevWrappers.join("|") !== nextWrappers.join("|");
}

function findPrevMeaningfulToken(tokens: Token[], index: number): Token | null {
  for (let i = index; i >= 0; i--) {
    const token = tokens[i];
    if (token.type === "space" || token.type === "other") continue;
    return token;
  }
  return null;
}

function findNextMeaningfulToken(tokens: Token[], index: number): Token | null {
  for (let i = index; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.type === "space" || token.type === "other") continue;
    return token;
  }
  return null;
}

function isInlineJoinCandidate(token: Token): boolean {
  return token.type === "text" || token.type === "element";
}

function getWrapperIds(token: Token): string[] {
  const wrappers = (token.meta?.wrappers as WrapperInfo[] | undefined) ?? [];
  return wrappers.map((wrapper) => wrapper.id);
}
